import { cache } from "react";
import memoize from "lodash/memoize";

import { CountryCode, isCountryCode } from "@/custom-types/CountryCode";
import { useSelector } from "@/redux/hooks";
import { getDomainCountryCode } from "@/redux/selectors/config";

const DOMAIN_COUNTRY_CODE_META_SELECTOR = "meta[name='x-country-code']";

let useCountryCode: () => string | undefined;

if (
  typeof window !== "undefined" &&
  document.querySelector(DOMAIN_COUNTRY_CODE_META_SELECTOR)
) {
  /**
   * For client-side / App Router
   *
   * In the App Router, we have to find a way to communicate headers that are
   * only available server-side to client-side code. This code checks for a
   * specific meta tag on the page that should be generated in either the
   * custom document (Pages Router) or root layout (App Router), both of which
   * are only rendered on the server:
   *
   *   <meta name="x-country-code" content="CA">
   */
  useCountryCode = memoize(
    () =>
      document.querySelector<HTMLMetaElement>(DOMAIN_COUNTRY_CODE_META_SELECTOR)
        ?.content,
  );
} else {
  /**
   * For client- and server-side / Pages Router
   *
   * This function encapsulates the domain country code that is currently stored in redux but should
   * be migrated to a different mechanism. The domain country code does not change
   * over the course of the page lifecycle, so does not need to be stored in redux.
   */
  useCountryCode = () => useSelector(getDomainCountryCode);
}

export default cache((): CountryCode => {
  const code = useCountryCode() || "XX";

  if (isCountryCode(code)) {
    return code as CountryCode;
  } else {
    return "US";
  }
});
